/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import qs from 'qs';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    string, bool, func, node, object,
} from 'prop-types';
import mbpLogger from 'mbp-logger';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';
import { MenuList } from '@material-ui/core';
import { flagOnQasFailed } from '../../../../../../state/ducks/Common/ducks/AddressSearchQAS/AddressSearchQAS-Actions';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getFeatureFlags } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import QasClient from '../../../../../../apis/qas-address-search-apis/qas-address-search-apis';

const styles = {
    wrapSuggestion: {
        position: 'relative',
    },
    autoSuggestion: {
        position: 'absolute',
        backgroundColor: '#FFF',
        zIndex: 3000,
        width: '100%',
        padding: '0px 3px',
    },
    addressList: {
        fontSize: '12px',
        padding: '4px',
        fontWeight: '600',
        borderBottom: 'solid 1px #ecdede',
    },
    menuListWrapper: {
        padding: '0px',
    },
};

const countryISOAlfa3Code = {
    US: 'USA',
    CA: 'CAN',
};

const countryISOAlfa2Code = {
    USA: 'US',
    CAN: 'CA',
};

const normalizeArrayToObject = (arr) => {
    const normalize = {};
    arr.forEach((item) => {
        normalize[[Object.keys(item)]] = item[[Object.keys(item)]];
    });
    return normalize;
};

const getGeoLocation = () => new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
        reject(new Error('noWindow'));
    } else if (navigator && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(resolve, reject);
    } else {
        reject(new Error('noSupport'));
    }
});

class AddressVerificationSearch extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            autoCompleteResult: {},
        };
        this.delayAutoComplete = null;
        this.location = {
            lat: '',
            long: '',
        };

        this.initGeoLocation = false;
    }

    componentDidUpdate(prevProps) {
        const {
            searchText, isFocus, countryCode, useGeoLocation,
        } = this.props;

        if (prevProps.searchText !== searchText && !isFocus) {
            this.searchAddress(searchText);
        }

        if (useGeoLocation && (prevProps.countryCode !== countryCode || !this.initGeoLocation)) {
            this.initGeoLocation = true;
            this.getUserLocation();
        }
    }

    getUserLocation = async () => {
        await getGeoLocation()
            .then((data) => {
                const { coords } = data;
                if (coords) {
                    const { latitude, longitude } = coords;
                    this.location = {
                        lat: `${latitude}`,
                        long: `${longitude}`,
                    };
                }
            }).catch((response) => {
                mbpLogger.logDebug({
                    function: '',
                    appName: process.env.npm_package_name,
                    module: 'getGeoLocation - AddressVerificationSearch',
                    message: 'User has rejected get the geolocation',
                    response,
                });
                this.location = {
                    lat: '',
                    long: '',
                };
            });
    }

    getSuggestions = () => {
        const { autoCompleteResult } = this.state;
        if (autoCompleteResult
            && autoCompleteResult.results
            && autoCompleteResult.count
            && autoCompleteResult.count > 0) {
            return autoCompleteResult.results.map((address) => (
                <MenuItem
                    style={styles.addressList}
                    key={`suggestionAVS${address.suggestion.replace(/\s/g, '')}`}
                    onClick={() => { this.handlePickAnAddress(address); }}
                >
                    {address.suggestion}
                </MenuItem>
            ));
        }

        if (autoCompleteResult
            && autoCompleteResult.results
            && autoCompleteResult.count === 0) {
            return (
                <MenuItem
                    style={styles.addressList}
                    key="No_Match_Found"
                    onClick={() => { this.handleNoMatch(); }}
                >
                    No address match found
                </MenuItem>
            );
        }
        return null;
    }

    handleNoMatch = () => {
        const { onBlurHandler } = this.props;
        this.resetAutoSearch();
        onBlurHandler();
    }

    returnSelectedAddress = (formattedAddress) => {
        const { getPreFillAddressDetails } = this.props;
        const { components, address } = formattedAddress;
        const nComponents = normalizeArrayToObject(components);
        const nAddress = normalizeArrayToObject(address);
        let address1 = nAddress.addressLine1;
        let address2 = `${nAddress.addressLine2} ${nAddress.addressLine3}`;
        if (['APO', 'FPO'].includes(nComponents.locality1)) {
            address1 = nComponents.street1;
            address2 = nComponents.deliveryService4;
        }
        const selectedAddress = {
            addressLine1: address1,
            addressLine2: address2,
            city: nAddress.locality,
            state: nAddress.province,
            country: countryISOAlfa2Code[nComponents.countryISO1],
            zipcode: nAddress.postalCode.split('-')[0],
        };
        getPreFillAddressDetails(selectedAddress);
        return selectedAddress;
    }

    handlePickAnAddress = (address) => {
        const { onBlurHandler, track } = this.props;
        if (address && address.format) {
            const { format } = address;
            const formatParams = qs.parse(format.substring(format.indexOf('?') + 1));
            QasClient.qasGet({}, 'format', formatParams).then((res) => {
                const addressData = this.returnSelectedAddress(res.data);
                this.resetAutoSearch();
                onBlurHandler();
                track({
                    eventCategory: 'Checkout',
                    eventAction: 'Selected Google Address Recommendation',
                    eventLabel: `${addressData?.addressLine1 || ''}, ${addressData?.city || ''} ${addressData?.state || ''} ${addressData?.zipcode || ''}`,
                });
            }).catch(() => {
                this.resetAutoSearch();
                onBlurHandler();
            });
        }
    }

    resetAutoSearch = () => {
        this.setState({
            autoCompleteResult: {},
        });
    }

    showSuggestion = () => {
        const { searchTextFocus, isFocus } = this.props;
        if (searchTextFocus || isFocus) {
            return true;
        }
        return false;
    }

    handleQASAddressFailed = () => {
        const { callFlagOnQasFailed, searchId } = this.props;
        callFlagOnQasFailed();
        setTimeout(() => {
            if (typeof document !== 'undefined' && document.getElementById(searchId)) {
                document.getElementById(searchId).focus();
            }
        }, 200);
    }

    searchAddress = (searchText) => {
        const { featureFlags } = this.props;
        const searchTimeout = featureFlags['qas-address-search-timeout-value'] || 5;
        const timeoutInSecs = searchTimeout * 1000;
        let addressSearchText = searchText || '';
        if (searchText.length >= 3) {
            const {
                countryCode, useGeoLocation,
                zipCode, isValidZipCode, state,
            } = this.props;
            const isoCountryCode = countryISOAlfa3Code[countryCode];

            if (isValidZipCode && zipCode && zipCode !== '00000') {
                const searchZipCode = zipCode.split('-')[0];
                let stateZipCode = '';

                if (state) stateZipCode = (`${stateZipCode} ${state}`).trim();
                if (searchZipCode) stateZipCode = (`${stateZipCode} ${searchZipCode}`).trim();

                addressSearchText = `${addressSearchText.trim()}, ${stateZipCode}`;
            }

            if (isoCountryCode) {
                const params = {
                    query: addressSearchText.trim(),
                    country: isoCountryCode,
                };
                if (countryCode === 'US' && useGeoLocation) {
                    const { lat, long } = this.location;
                    if (lat && long) {
                        params.location = `${lat},${long}`;
                    }
                }
                QasClient.qasGet({}, 'search', params, timeoutInSecs).then((res) => {
                    this.setState({
                        autoCompleteResult: res.data,
                    });
                }).catch(() => {
                    this.resetAutoSearch();
                    this.handleQASAddressFailed();
                });
            }
        } else {
            this.resetAutoSearch();
        }
    }

    handleClickAway = () => {
        const { onBlurHandler } = this.props;
        onBlurHandler();
    }

    handleAutoCompleteBlur = () => {
        if (typeof window !== 'undefined') {
            const { onBlurHandler } = this.props;
            if (this.delayAutoComplete) clearInterval(this.delayAutoComplete);
            this.delayAutoComplete = setTimeout(() => {
                onBlurHandler();
            }, 700); // 700 IE11
        }
    }

    render() {
        const {
            searchId,
            children,
            isFocus,
        } = this.props;

        return (
            <div id={`auto_${searchId}`} onBlur={this.handleAutoCompleteBlur}>
                <ClickAwayListener onClickAway={this.handleClickAway}>
                    <div>
                        {children}
                        {this.showSuggestion()
                            && (
                                <div style={styles.wrapSuggestion}>
                                    <Paper style={styles.autoSuggestion}>
                                        <MenuList style={styles.menuListWrapper} autoFocusItem={isFocus}>
                                            { this.getSuggestions() }
                                        </MenuList>
                                    </Paper>
                                </div>
                            )}
                    </div>
                </ClickAwayListener>
            </div>
        );
    }
}

AddressVerificationSearch.defaultProps = {
    searchTextFocus: false,
    onBlurHandler: () => {},
    useGeoLocation: false,
    isFocus: false,
    zipCode: '',
    isValidZipCode: false,
    state: '',
    countryCode: 'US',
    track: () => {},
};

AddressVerificationSearch.propTypes = {
    getPreFillAddressDetails: func.isRequired,
    searchText: string.isRequired,
    searchTextFocus: bool,
    searchId: string.isRequired,
    children: node.isRequired,
    onBlurHandler: func,
    useGeoLocation: bool,
    isFocus: bool,
    callFlagOnQasFailed: func.isRequired,
    zipCode: string,
    isValidZipCode: bool,
    state: string,
    countryCode: string,
    track: func,
    featureFlags: object.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
    callFlagOnQasFailed: bindActionCreators(flagOnQasFailed, dispatch),
    track: bindActionCreators(trackEvent, dispatch),
});
const mapStateToProps = (state) => ({
    featureFlags: getFeatureFlags(state),
});
export default connect(mapStateToProps, mapDispatchToProps)(AddressVerificationSearch);
